/**
 * Token Storage Types
 *
 * This file contains types for secure token storage implementations across platforms.
 * These types define the contract for token management including storage, validation,
 * and lifecycle management.
 *
 * @module types/auth/storage
 */

import { dayjs } from '../../utils/date';

// =============================================================================
// TOKEN TYPE DEFINITIONS
// =============================================================================

/**
 * Supported token types for authentication.
 *
 * @example
 * ```typescript
 * import type { TokenType } from '@shared/types/auth';
 *
 * const tokenType: TokenType = 'Bearer';
 * ```
 */
export type TokenType = 'Bearer' | 'Basic';

/**
 * Default token type used for authentication.
 *
 * @example
 * ```typescript
 * import { DEFAULT_TOKEN_TYPE } from '@shared/types/auth';
 *
 * const headers = {
 *   Authorization: `${DEFAULT_TOKEN_TYPE} ${accessToken}`,
 * };
 * ```
 */
export const DEFAULT_TOKEN_TYPE: TokenType = 'Bearer';

/**
 * Storage mechanism types for token persistence.
 *
 * @example
 * ```typescript
 * import type { StorageType } from '@shared/types/auth';
 *
 * const storageType: StorageType = 'secure';
 * ```
 */
export type StorageType =
  'secure' | 'memory' | 'cookies' | 'keychain' | 'keystore';

// =============================================================================
// TOKEN DATA INTERFACES
// =============================================================================

/**
 * Token data structure containing access and refresh tokens with metadata.
 *
 * This interface represents the complete token data stored by the token storage service.
 * It includes timestamps for expiration validation and the token type for authorization headers.
 *
 * @example
 * ```typescript
 * import type { TokenData } from '@shared/types/auth';
 *
 * const tokenData: TokenData = {
 *   accessToken: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
 *   refreshToken: 'dGhpcyBpcyBhIHJlZnJlc2ggdG9rZW4...',
 *   expiresAt: dayjs().add(1, 'hour').valueOf(),
 *   issuedAt: dayjs().valueOf(),
 *   tokenType: 'Bearer',
 * };
 * ```
 */
export interface TokenData {
  /** The access token string (usually a JWT) */
  accessToken: string;

  /** Optional refresh token for obtaining new access tokens */
  refreshToken?: string | undefined;

  /** Timestamp (ms) when the access token expires */
  expiresAt: number;

  /** Timestamp (ms) when the token was issued */
  issuedAt: number;

  /** The type of token (Bearer or Basic) */
  tokenType: TokenType;
}

/**
 * Result of token validation including expiration and refresh status.
 *
 * This interface is returned when validating a token to determine if it's
 * still valid, expired, or should be refreshed proactively.
 *
 * @example
 * ```typescript
 * import type { TokenValidationResult } from '@shared/types/auth';
 *
 * const validation: TokenValidationResult = await tokenService.validateToken();
 * if (!validation.isValid) {
 *   await authService.logout();
 * } else if (validation.shouldRefresh) {
 *   await authService.refreshToken();
 * }
 * ```
 */
export interface TokenValidationResult {
  /** Whether the token is currently valid (not expired) */
  isValid: boolean;

  /** Whether the token has expired */
  isExpired: boolean;

  /** Time remaining until expiration in milliseconds */
  expiresIn: number;

  /** Whether the token should be refreshed (based on threshold) */
  shouldRefresh: boolean;
}

/**
 * Configuration options for the token storage service.
 *
 * These options control encryption, refresh timing, retry behavior,
 * and storage key prefixes.
 *
 * @example
 * ```typescript
 * import type { TokenStorageOptions } from '@shared/types/auth';
 *
 * const options: TokenStorageOptions = {
 *   enableEncryption: true,
 *   refreshThreshold: 10, // Refresh 10 minutes before expiration
 *   maxRetryAttempts: 3,
 *   storagePrefix: 'myapp_',
 * };
 * ```
 */
export interface TokenStorageOptions {
  /** Whether to encrypt tokens before storage */
  enableEncryption?: boolean;

  /** Minutes before expiration to trigger refresh */
  refreshThreshold?: number;

  /** Maximum number of retry attempts for storage operations */
  maxRetryAttempts?: number;

  /** Prefix for storage keys to avoid conflicts */
  storagePrefix?: string;
}

/**
 * Information about the token storage state for debugging.
 *
 * This interface provides a snapshot of the current token storage state
 * including what storage mechanism is being used and token metadata.
 *
 * @example
 * ```typescript
 * import type { TokenStorageInfo } from '@shared/types/auth';
 *
 * const info: TokenStorageInfo = await tokenService.getStorageInfo();
 * console.log(`Using ${info.storageType} storage`);
 * console.log(`Tokens present: ${info.hasTokens}`);
 * if (info.expiresAt) {
 *   console.log(`Expires: ${info.expiresAt}`);
 * }
 * ```
 */
export interface TokenStorageInfo {
  /** Whether tokens are currently stored */
  hasTokens: boolean;

  /** The type of token stored (e.g., 'Bearer') */
  tokenType: string | null;

  /** ISO string of when the token expires */
  expiresAt: string | null;

  /** ISO string of when the token was issued */
  issuedAt: string | null;

  /** Whether the token is currently expired */
  isExpired: boolean;

  /** Milliseconds until expiration (0 if expired) */
  expiresIn: number;

  /** The storage mechanism being used */
  storageType: StorageType;
}

/**
 * Token metadata for backup and migration purposes.
 *
 * This interface contains non-sensitive token metadata that can be
 * exported for debugging or migration without exposing the actual tokens.
 *
 * @example
 * ```typescript
 * import type { TokenMetadata } from '@shared/types/auth';
 *
 * const metadata: TokenMetadata = await tokenService.exportTokenMetadata();
 * console.log('Token metadata:', JSON.stringify(metadata));
 * ```
 */
export interface TokenMetadata {
  /** Whether tokens are currently stored */
  hasTokens: boolean;

  /** Timestamp of when the token expires (null if no token) */
  expiresAt: number | null;

  /** Timestamp of when the token was issued (null if no token) */
  issuedAt: number | null;

  /** The type of token stored (null if no token) */
  tokenType: string | null;
}

// =============================================================================
// TOKEN STORAGE SERVICE INTERFACE
// =============================================================================

/**
 * Interface for secure token storage implementations.
 *
 * This interface defines the contract that all platform-specific
 * token storage implementations must follow to ensure consistent
 * behavior across web and mobile platforms.
 *
 * @example
 * ```typescript
 * import type { ITokenStorageService } from '@shared/types/auth';
 *
 * class WebTokenStorage implements ITokenStorageService {
 *   async setTokens(accessToken: string, refreshToken?: string): Promise<boolean> {
 *     // Store in secure cookie
 *     return true;
 *   }
 *
 *   async getAccessToken(): Promise<string | null> {
 *     // Retrieve from secure cookie
 *     return null;
 *   }
 *
 *   // ... implement other methods
 * }
 * ```
 */
export interface ITokenStorageService {
  /**
   * Store access and refresh tokens securely.
   *
   * @param accessToken - The access token to store
   * @param refreshToken - Optional refresh token to store
   * @param customExpiration - Optional custom expiration time in milliseconds
   * @returns Promise resolving to true if tokens were stored successfully
   */
  setTokens(
    accessToken: string,
    refreshToken?: string,
    customExpiration?: number
  ): Promise<boolean>;

  /**
   * Retrieve the current access token.
   *
   * @returns Promise resolving to the access token if valid, null otherwise
   */
  getAccessToken(): Promise<string | null>;

  /**
   * Retrieve the current refresh token.
   *
   * @returns Promise resolving to the refresh token if available, null otherwise
   */
  getRefreshToken(): Promise<string | null>;

  /**
   * Get complete token data with metadata.
   *
   * @returns Promise resolving to TokenData if available, null otherwise
   */
  getTokenData(): Promise<TokenData | null>;

  /**
   * Validate current token and return validation result.
   *
   * @returns Promise resolving to TokenValidationResult with expiration info
   */
  validateToken(): Promise<TokenValidationResult>;

  /**
   * Clear all stored tokens.
   *
   * @returns Promise resolving to true if tokens were cleared successfully
   */
  clearTokens(): Promise<boolean>;

  /**
   * Check if tokens are currently stored.
   *
   * @returns Promise resolving to true if tokens exist
   */
  hasTokens(): Promise<boolean>;

  /**
   * Check if current token is expired.
   *
   * @returns Promise resolving to true if token is expired
   */
  isTokenExpired(): Promise<boolean>;

  /**
   * Get time until token expiration in milliseconds.
   *
   * @returns Promise resolving to time until expiration, 0 if expired
   */
  getTimeUntilExpiration(): Promise<number>;

  /**
   * Get expiration time as a dayjs value.
   *
   * @returns Promise resolving to the expiration, null if no token
   */
  getExpirationTime(): Promise<dayjs.Dayjs | null>;

  /**
   * Get storage information for debugging.
   *
   * @returns Promise resolving to TokenStorageInfo with token metadata
   */
  getStorageInfo(): Promise<TokenStorageInfo>;

  /**
   * Export token metadata for backup/migration (excluding sensitive data).
   *
   * @returns Promise resolving to TokenMetadata object
   */
  exportTokenMetadata(): Promise<TokenMetadata>;

  /**
   * Clear all storage (including memory fallback).
   *
   * @returns Promise resolving to true if all storage was cleared
   */
  clearAllStorage(): Promise<boolean>;
}

// =============================================================================
// DEFAULT CONFIGURATION
// =============================================================================

/**
 * Default configuration values for TokenStorageOptions.
 *
 * These values are used when creating a token storage service without
 * explicit options.
 *
 * @example
 * ```typescript
 * import { DEFAULT_TOKEN_STORAGE_OPTIONS } from '@shared/types/auth';
 *
 * const options = {
 *   ...DEFAULT_TOKEN_STORAGE_OPTIONS,
 *   refreshThreshold: 10, // Override default
 * };
 * ```
 */
export const DEFAULT_TOKEN_STORAGE_OPTIONS: Required<TokenStorageOptions> = {
  enableEncryption: true,
  refreshThreshold: 5, // Refresh 5 minutes before expiration
  maxRetryAttempts: 3,
  storagePrefix: 'pt_app_', // Personal Trainer App prefix
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Generate a storage key with the specified prefix.
 *
 * @param key - The base key name
 * @param prefix - The prefix to prepend
 * @returns The prefixed storage key
 *
 * @example
 * ```typescript
 * import { getStorageKey, DEFAULT_TOKEN_STORAGE_OPTIONS } from '@shared/types/auth';
 *
 * const key = getStorageKey('accessToken', DEFAULT_TOKEN_STORAGE_OPTIONS.storagePrefix);
 * // Returns: 'pt_app_accessToken'
 * ```
 */
export const getStorageKey = (key: string, prefix: string): string => {
  return `${prefix}${key}`;
};

/**
 * Parse a JWT token to extract the expiration time.
 *
 * @param token - The JWT token string to parse
 * @returns The expiration timestamp in milliseconds, or null if parsing fails
 *
 * @example
 * ```typescript
 * import { parseJwtExpiration } from '@shared/types/auth';
 *
 * const expiresAt = parseJwtExpiration(accessToken);
 * if (expiresAt) {
 *   const isExpired = dayjs().valueOf() > expiresAt;
 * }
 * ```
 */
export const parseJwtExpiration = (token: string): number | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;

    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : null; // Convert to milliseconds
  } catch {
    return null;
  }
};

/**
 * Create a token validation result from token data.
 *
 * @param tokenData - The token data to validate (null if no token)
 * @param refreshThreshold - Minutes before expiration to suggest refresh (default: 5)
 * @returns TokenValidationResult with validation status
 *
 * @example
 * ```typescript
 * import { createTokenValidationResult } from '@shared/types/auth';
 *
 * const tokenData = await storage.getTokenData();
 * const validation = createTokenValidationResult(tokenData, 10);
 *
 * if (validation.shouldRefresh) {
 *   console.log(`Token expires in ${validation.expiresIn}ms, refreshing...`);
 *   await refreshToken();
 * }
 * ```
 */
export const createTokenValidationResult = (
  tokenData: TokenData | null,
  refreshThreshold: number = 5
): TokenValidationResult => {
  if (!tokenData) {
    return {
      isValid: false,
      isExpired: true,
      expiresIn: 0,
      shouldRefresh: false,
    };
  }

  const now = dayjs().valueOf();
  const isExpired = now >= tokenData.expiresAt;
  const expiresIn = Math.max(0, tokenData.expiresAt - now);
  const thresholdMs = refreshThreshold * 60 * 1000;
  const shouldRefresh = !isExpired && expiresIn <= thresholdMs;

  return {
    isValid: !isExpired,
    isExpired,
    expiresIn,
    shouldRefresh,
  };
};
